/**
 * LgpdRequestsListView — Lista de LGPDRequests com filtros.
 *
 * Pattern Opção A (Capability-driven UI): botão "Aprovar" só aparece
 * se `lgpd-requests-approve`; botão "Executar" só se `lgpd-requests-execute`.
 *
 * D-B defense-in-depth: sem dados sensíveis do titular na linha da lista
 * (apenas initials mascarados, tipo, status, prazo). Detalhe fica no Drawer.
 *
 * P15 minimização: CPF mascarado (***.***.***-**) na listagem.
 * P17: toda ação muta status + emite audit (server-side).
 *
 * Brief 5 B1 — design canônico.
 */

import { CapabilityGate } from '../../../components/CapabilityGate';

import { LgpdRequestStatusBadge, REQUEST_TYPE_LABEL } from './LgpdRequestStatusBadge';

import type { LgpdRequestStatus, LgpdRequestType, LgpdRequestWireItem } from './types';

interface LgpdRequestsListViewProps {
  requests: LgpdRequestWireItem[];
  isLoading: boolean;
  isError: boolean;
  filterStatus: LgpdRequestStatus | 'all';
  filterType: LgpdRequestType | 'all';
  onFilterStatusChange: (status: LgpdRequestStatus | 'all') => void;
  onFilterTypeChange: (type: LgpdRequestType | 'all') => void;
  onOpenDetail: (request: LgpdRequestWireItem) => void;
  /**
   * Quando definido, a lista está travada num único tipo (ex.: aba Oposições):
   * esconde o seletor de Tipo — o filtro de tipo já é fixo upstream.
   */
  lockedType?: LgpdRequestType;
}

const STATUS_FILTERS: { value: LgpdRequestStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'received', label: 'Recebidas' },
  { value: 'identity_validation', label: 'Validando identidade' },
  { value: 'identity_validated', label: 'Identidade validada' },
  { value: 'in_processing', label: 'Em processamento' },
  { value: 'pending_information', label: 'Pendente info' },
  { value: 'completed', label: 'Concluídas' },
  { value: 'rejected_by_law', label: 'Rejeitadas' },
];

const TYPE_FILTERS: { value: LgpdRequestType | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'access', label: 'Acesso' },
  { value: 'correction', label: 'Correção' },
  { value: 'anonymization', label: 'Eliminação ⚠' },
  { value: 'portability', label: 'Portabilidade' },
  { value: 'opposition', label: 'Oposição' },
];

// Estados terminais (ADR-098): prazo deixa de ser relevante.
const TERMINAL_STATUSES: LgpdRequestStatus[] = [
  'completed',
  'rejected_by_law',
  'cancelled_by_requester',
  'identity_rejected',
];

export function LgpdRequestsListView({
  requests,
  isLoading,
  isError,
  filterStatus,
  filterType,
  onFilterStatusChange,
  onFilterTypeChange,
  onOpenDetail,
  lockedType,
}: LgpdRequestsListViewProps): JSX.Element {
  return (
    <div>
      {/* Filtros */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginRight: 4,
          }}
        >
          Status:
        </span>
        {STATUS_FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            label={f.label}
            active={filterStatus === f.value}
            onClick={() => {
              onFilterStatusChange(f.value);
            }}
          />
        ))}

        {lockedType === undefined && (
          <>
            <div style={{ width: 1, height: 16, backgroundColor: '#e5e7eb', margin: '0 4px' }} />

            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginRight: 4,
              }}
            >
              Tipo:
            </span>
            {TYPE_FILTERS.map((f) => (
              <FilterChip
                key={f.value}
                label={f.label}
                active={filterType === f.value}
                onClick={() => {
                  onFilterTypeChange(f.value);
                }}
              />
            ))}
          </>
        )}
      </div>

      {/* Loading */}
      {isLoading && <RequestsListSkeleton />}

      {/* Error */}
      {isError && !isLoading && (
        <div
          style={{
            padding: '24px',
            textAlign: 'center',
            color: '#ef4444',
            fontSize: 13,
          }}
        >
          Erro ao carregar solicitações LGPD. Tente recarregar.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && requests.length === 0 && (
        <div
          style={{
            padding: '32px 24px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 13,
          }}
        >
          Nenhuma solicitação encontrada com os filtros aplicados.
        </div>
      )}

      {/* Lista */}
      {!isLoading && !isError && requests.length > 0 && (
        <div
          role="list"
          aria-label="Solicitações LGPD"
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {requests.map((req) => (
            <LgpdRequestRow key={req.id} request={req} onOpen={onOpenDetail} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LgpdRequestRow ───────────────────────────────────────────────────────────

function LgpdRequestRow({
  request,
  onOpen,
}: {
  request: LgpdRequestWireItem;
  onOpen: (r: LgpdRequestWireItem) => void;
}): JSX.Element {
  const isElimination = request.requestType === 'anonymization';
  // O backend já entrega o nº de dias até o prazo legal (serializer).
  const daysLeft = request.daysUntilDeadline;
  const deadlineUrgent = daysLeft <= 7;
  const deadlineCritical = daysLeft <= 3;
  // Prazo só é relevante enquanto a solicitação está aberta (não-terminal).
  const isTerminal = TERMINAL_STATUSES.includes(request.status);

  const requesterName = request.requesterSelfDeclaredData?.name ?? 'Titular não identificado';
  // P15 minimização: mascarar CPF na listagem
  const cpfMasked = request.requesterSelfDeclaredData?.cpf
    ? `***.***.***-${request.requesterSelfDeclaredData.cpf.slice(-2)}`
    : '—';

  return (
    <button
      type="button"
      role="listitem"
      aria-label={`Abrir solicitação ${REQUEST_TYPE_LABEL[request.requestType] ?? request.requestType} de ${requesterName}`}
      onClick={() => {
        onOpen(request);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        background: '#fff',
        border: '1px solid',
        borderColor: isElimination ? '#fca5a5' : '#e5e7eb',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'box-shadow 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Ícone tipo */}
      <RequestTypeIcon type={request.requestType} />

      {/* Titular info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#111827',
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {requesterName}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
          CPF {cpfMasked} · {REQUEST_TYPE_LABEL[request.requestType] ?? request.requestType}
        </div>
      </div>

      {/* Status badge */}
      <LgpdRequestStatusBadge status={request.status} />

      {/* Prazo (só enquanto aberta) */}
      {!isTerminal && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: deadlineCritical ? '#ef4444' : deadlineUrgent ? '#f59e0b' : '#6b7280',
            minWidth: 60,
            textAlign: 'right',
          }}
          aria-label={`${daysLeft} dias restantes`}
        >
          {daysLeft}d restantes
        </span>
      )}

      {/* Arrow */}
      <CapabilityGate capability="lgpd-requests-read">
        <span style={{ color: '#9ca3af', fontSize: 16 }} aria-hidden="true">
          ›
        </span>
      </CapabilityGate>
    </button>
  );
}

// ─── RequestTypeIcon ──────────────────────────────────────────────────────────

function RequestTypeIcon({ type }: { type: string }): JSX.Element {
  const icons: Record<string, { emoji: string; bg: string }> = {
    access: { emoji: '📄', bg: '#eff6ff' },
    correction: { emoji: '✏️', bg: '#f0fdf4' },
    anonymization: { emoji: '⚠️', bg: '#fef2f2' },
    portability: { emoji: '⇄', bg: '#fdf4ff' },
    opposition: { emoji: '🛡', bg: '#fff7ed' },
    consent_withdrawal: { emoji: '✕', bg: '#fef9c3' },
  };
  const cfg = icons[type] ?? { emoji: '?', bg: '#f9fafb' };
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: cfg.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      {cfg.emoji}
    </div>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        padding: '3px 10px',
        borderRadius: 20,
        border: '1px solid',
        borderColor: active ? '#6366f1' : '#d1d5db',
        backgroundColor: active ? '#ede9fe' : '#fff',
        color: active ? '#4f46e5' : '#374151',
        fontSize: 11,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function RequestsListSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Carregando solicitações LGPD...">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 60,
            borderRadius: 8,
            backgroundColor: '#f3f4f6',
            marginBottom: 6,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}
